// Персистентное (в рамках одной партии) стратегическое состояние ботов.
// Все данные здесь СОВЕЩАТЕЛЬНЫЕ: каждый тик решения перепроверяются по свежему
// состоянию redux, поэтому потеря/устаревание записи деградирует к жадному
// поведению, но никогда не приводит к нелегальным действиям.

const strategyByPlayerId = new Map();
let currentGameKey = null;

/**
 * Отпечаток партии: смена состава игроков означает новую партию —
 * стратегическое состояние прошлой игры сбрасывается.
 */
export const calcGameKey = (players) => {
  return (players || []).map((player) => `${player.id}:${player.factionId}`).join('|');
};

export const ensureGameStrategyReset = (players) => {
  const gameKey = calcGameKey(players);
  if (gameKey !== currentGameKey) {
    strategyByPlayerId.clear();
    currentGameKey = gameKey;
    return true;
  }
  return false;
};

// Глубокая копия стратегической записи (Map кампаний и вложенные объекты)
const cloneStrategyValue = (strategy) => {
  const cloned = {...strategy};
  if (strategy.campaigns instanceof Map) {
    cloned.campaigns = new Map(
      [...strategy.campaigns.entries()].map(([unitId, campaign]) => [unitId, {...campaign}])
    );
  }
  if (Array.isArray(strategy.recentBuildCells)) {
    cloned.recentBuildCells = [...strategy.recentBuildCells];
  }
  if (strategy.savingsGoal) {
    cloned.savingsGoal = {...strategy.savingsGoal};
  }
  // Волна 32 (логистика): черный список застрявших кампаний — глубокая копия,
  // иначе песочница сильного режима меняла бы реальную запись. Поля прогресса
  // кампании (bestDistance, progressRound) — примитивы, их копирует {...campaign}
  if (strategy.campaignStalls instanceof Map) {
    cloned.campaignStalls = new Map(
      [...strategy.campaignStalls.entries()].map(([unitId, stall]) => [unitId, {...stall, cells: [...stall.cells]}])
    );
  }
  // Волна 35 (G): назначение гарнизонов на ход войны ({round, assignments}) —
  // глубокая копия: песочница сильного режима гоняет перемещение соперников-ботов,
  // и их пересчет не должен подменять реальное назначение
  if (strategy.w35Garrisons) {
    cloned.w35Garrisons = {
      ...strategy.w35Garrisons,
      assignments: (strategy.w35Garrisons.assignments || []).map((entry) => ({
        ...entry,
        quarters: [...(entry.quarters || [])],
        holderIds: [...(entry.holderIds || [])],
      })),
    };
  }
  // Волна 35 (S): монеты на начало хода стройки ({round, coins}) — своя копия записи
  if (strategy.w35ConstructionPhaseCoins) {
    cloned.w35ConstructionPhaseCoins = {...strategy.w35ConstructionPhaseCoins};
  }
  return cloned;
};

/**
 * Снимок/восстановление стратегического состояния всех ботов — для песочницы
 * «сильного режима»: симуляция ходов противников не должна портить их реальные
 * кампании/накопления/архетипы.
 */
export const snapshotBotStrategies = () => ({
  gameKey: currentGameKey,
  entries: [...strategyByPlayerId.entries()].map(([playerId, strategy]) => [
    playerId, cloneStrategyValue(strategy),
  ]),
});

export const restoreBotStrategies = (snapshot) => {
  strategyByPlayerId.clear();
  currentGameKey = snapshot.gameKey;
  snapshot.entries.forEach(([playerId, strategy]) => {
    strategyByPlayerId.set(playerId, cloneStrategyValue(strategy));
  });
};

export const getBotStrategy = (playerId) => {
  if (!strategyByPlayerId.has(playerId)) {
    strategyByPlayerId.set(playerId, {
      archetype: null,
      archetypeRound: 0,
      savingsGoal: null, // {token, cellKey, price, round}
      campaigns: new Map(), // unitId -> {type: 'capture'|'siege'|'defense', targetHexKey, round}
      recentBuildCells: [], // фронт строительства: ячейки последних построек (FIFO)
      policyPurchases: 0, // покупки политик за партию (дисциплина смены у людей)
    });
  }
  return strategyByPlayerId.get(playerId);
};

// Сколько последних построек составляют "фронт строительства" для тяготения армии
const RECENT_BUILD_CELLS_LIMIT = 8;

export const rememberBuildCell = (playerId, cellKey) => {
  const strategy = getBotStrategy(playerId);
  if (!strategy.recentBuildCells) {
    strategy.recentBuildCells = [];
  }
  strategy.recentBuildCells = strategy.recentBuildCells.filter((key) => key !== cellKey);
  strategy.recentBuildCells.push(cellKey);
  if (strategy.recentBuildCells.length > RECENT_BUILD_CELLS_LIMIT) {
    strategy.recentBuildCells.shift();
  }
};
